/**
 * Raw brand colours — theme-invariant. Use only where a colour is genuinely
 * fixed. Everything else reads the semantic {@link ThemeColors} set via
 * `useTheme()` so it follows light / dark.
 */
export const palette = {
  brandMidnight: "#013D5A",
  brandHerb: "#708C69",
  brandLionsmane: "#FCF3E3",
  brandCeleste: "#BDD3CE",
  brandMarigold: "#F4A258",
} as const;

export const spacing = {
  xs: 4,
  s: 8,
  m: 12,
  l: 16,
  xl: 24,
  xxl: 32,
  xxxl: 48,
} as const;

export const radii = {
  xs: 2,
  s: 8,
  m: 12,
  l: 20,
  xl: 999,
  control: 10,
  card: 16,
  pill: 999,
} as const;

export const fonts = {
  regular: "DMSans_400Regular",
  medium: "DMSans_500Medium",
  semibold: "Fraunces_600SemiBold",
  bold: "Fraunces_700Bold",
  mono: "DMSans_400Regular",
  monoBold: "DMSans_700Bold",
} as const;

/**
 * Shared type ramp (see DESIGN.md). Display/headings use Fraunces, body/UI DM Sans.
 * Headings track -0.01em; muted body text uses `colors.mutedForeground` (solid).
 */
export const type = {
  display: {
    fontFamily: fonts.bold,
    fontSize: 40,
    lineHeight: 44,
    letterSpacing: -0.5,
  },
  h1: {
    fontFamily: fonts.bold,
    fontSize: 30,
    lineHeight: 35,
    letterSpacing: -0.3,
  },
  h2: {
    fontFamily: fonts.semibold,
    fontSize: 22,
    lineHeight: 28,
    letterSpacing: -0.2,
  },
  h3: {
    fontFamily: fonts.semibold,
    fontSize: 18,
    lineHeight: 24,
    letterSpacing: -0.1,
  },
  body: { fontFamily: fonts.regular, fontSize: 16, lineHeight: 26 },
  sm: { fontFamily: fonts.regular, fontSize: 14, lineHeight: 22 },
  xs: { fontFamily: fonts.medium, fontSize: 12, lineHeight: 17 },
} as const;

/**
 * Elevation presets (see DESIGN.md). `boxShadow` is supported cross-platform on
 * this React Native version and keeps the values identical to the website.
 */
export const shadows = {
  sm: { boxShadow: "0 1px 2px rgba(1, 61, 90, 0.08)" },
  card: {
    boxShadow:
      "0 1px 3px rgba(1, 61, 90, 0.06), 0 6px 20px rgba(1, 61, 90, 0.10)",
  },
  raised: {
    boxShadow:
      "0 4px 12px rgba(1, 61, 90, 0.10), 0 24px 48px rgba(1, 61, 90, 0.18)",
  },
} as const;

export type ColorScheme = "light" | "dark";

/** Semantic colour contract — key names shared across the app fleet, plus
 *  Florafolio's `*Nav*` and `backgroundSunken` / `accentSoft` extensions. */
export type ThemeColors = {
  background: string;
  backgroundSunken: string;
  foreground: string;
  heading: string;
  elevated: string;
  surface: string;
  mutedSurface: string;
  mutedForeground: string;
  surfaceNav: string;
  navActiveSurface: string;
  navActiveIndicator: string;
  primary: string;
  primaryHover: string;
  primaryForeground: string;
  secondary: string;
  secondaryForeground: string;
  accent: string;
  accentSoft: string;
  border: string;
  borderStrong: string;
  focusRing: string;
  success: string;
  successSurface: string;
  warning: string;
  danger: string;
  dangerSurface: string;
  overlay: string;
};

export const lightColors: ThemeColors = {
  background: "#FCF3E3",
  backgroundSunken: "#EAECDC",
  foreground: "#013D5A",
  heading: "#013D5A",
  elevated: "#FFFFFF",
  surface: "#E8F0ED",
  mutedSurface: "#D9E8E3",
  mutedForeground: "#4A6570",
  surfaceNav: "#E3E7DB",
  navActiveSurface: "#FFFFFF",
  navActiveIndicator: "#F4A258",
  primary: "#708C69",
  primaryHover: "#5F7859",
  primaryForeground: "#FFFFFF",
  secondary: "#BDD3CE",
  secondaryForeground: "#013D5A",
  accent: "#F4A258",
  accentSoft: "#FBE4CE",
  border: "#C5D4CF",
  borderStrong: "#A9BFB9",
  focusRing: "rgba(112, 140, 105, 0.55)",
  success: "#708C69",
  successSurface: "#E6F0E7",
  warning: "#F4A258",
  danger: "#b42318",
  dangerSurface: "#fdf0ee",
  overlay: "rgba(1, 61, 90, 0.45)",
};

/**
 * Dark palette — derived from the light "midnight / herb / lionsmane /
 * marigold" set (3 Sep 2026, pending Brad's review in the running app): deep
 * midnight-teal grounds, lionsmane-cream text, herb green and marigold lifted.
 */
export const darkColors: ThemeColors = {
  background: "#0B1F2A",
  backgroundSunken: "#081820",
  foreground: "#E9E3D3",
  heading: "#FCF3E3",
  elevated: "#132A36",
  surface: "#16303C",
  mutedSurface: "#1E3A47",
  mutedForeground: "#9DB0B5",
  surfaceNav: "#112530",
  navActiveSurface: "#1B3642",
  navActiveIndicator: "#F4A258",
  primary: "#8CA985",
  primaryHover: "#7B9874",
  primaryForeground: "#0B1F2A",
  secondary: "#3A4F4A",
  secondaryForeground: "#E9E3D3",
  accent: "#F4A258",
  accentSoft: "#3A2C1C",
  border: "#2C4550",
  borderStrong: "#3E5964",
  focusRing: "rgba(140, 169, 133, 0.55)",
  success: "#8CA985",
  successSurface: "#1A3128",
  warning: "#F4A258",
  danger: "#F0736A",
  dangerSurface: "#3A1F1D",
  overlay: "rgba(0, 0, 0, 0.55)",
};

export const tokens = {
  palette,
  spacing,
  radii,
  fonts,
  type,
  shadows,
  lightColors,
  darkColors,
} as const;
